import React from "react";
import { useTranslation } from "react-i18next";
import { Button, Tooltip, theme } from "antd";
import {
  ArrowRightOutlined,
  BellOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  WarningOutlined,
} from "@ant-design/icons";

/**
 * Where the case is, and what this person owes it — one surface.
 *
 * This replaces two stacked antd Alerts: a blue "Current stage: X" and an amber
 * "You have sections to fill". They were competing for the same glance while
 * answering two halves of one question, and between them they pushed the actual
 * form a third of a screen down. Worse, "Current stage: Hazards & controls" is
 * a fact with no context — it names a step without saying how many came before
 * it or what is left, which is the only reason anyone reads it.
 *
 * So the stage line becomes a tracker across the whole process, and the "your
 * turn" call sits under a hairline inside the same panel: one border, one
 * reading order, position first and obligation second.
 *
 * Above both sits `blocking` -- one sentence naming whose move it is, computed
 * on the server. The tracker says WHERE the case is; that line says WHO it is
 * waiting for, which no arrangement of the tracker has ever been able to.
 */

type Stage = {
  key: string;
  name: string;
  kind?: string;
  /** false when this permit's own answers rule the stage out. */
  applies?: boolean;
  /** The rule in words, for the tooltip on a ruled-out stage. */
  condition_text?: string;
};

type Props = {
  /** Every stage in order, *including* the ones ruled out. */
  stages: Stage[];
  currentStageKey: string;
  /** Required sections open to this user right now and not yet submitted. */
  pending: any[];
  onJumpToSection: (formId: number) => void;
  /**
   * What the case is waiting on once data entry is finished and nothing on this
   * page will move it by itself.
   *
   * It earns a place here because the stage tracker is otherwise silent at
   * exactly the moment it is most confusing. Every section reads Submitted, the
   * tracker still points at the last data-entry stage — because that is where
   * the case genuinely sits until somebody performs the transition — and the
   * only control that does it lives in the Actions menu, unopened. People read
   * that as the permit being stuck.
   *
   * `tone` splits the two things that can be true there: `ready` (perform the
   * transition) and `blocked` (something is still missing, and the button goes
   * to it rather than into a server error).
   */
  /**
   * One sentence saying why the case is not moving, computed by the server
   * (`main/blocking.py`) from the same functions it enforces with.
   *
   * It sits above the tracker rather than below it because it is the thing
   * people open a case to find out, and everything else on this panel is the
   * evidence for it. The tracker says *where* the case is; this says *whose
   * move it is*, which the tracker has never been able to.
   *
   * `null` from a list payload means NOT COMPUTED and nothing is rendered --
   * never "nothing is blocking it".
   */
  blocking?: {
    kind: string;
    message: string;
    people: string[];
  } | null;
  nextStep?: {
    tone: "ready" | "blocked";
    message: string;
    label: string;
    busy?: boolean;
    onClick: () => void;
  } | null;
};

type StageState = "done" | "current" | "upcoming" | "skipped";

const RequestProgress: React.FC<Props> = ({
  stages,
  currentStageKey,
  pending,
  onJumpToSection,
  blocking,
  nextStep,
}) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();

  // Whether the line is about the reader. The server decides this, not the
  // page: it is the one that knows which slot is whose. Two `kind`s rather
  // than a boolean flag, so a new kind cannot silently default to "yours".
  const mine =
    blocking?.kind === "your_signature" || blocking?.kind === "your_section";
  // `blocking` names the very section the buttons below jump to, so the amber
  // caption under it would be the third time the page says one thing.
  const blockingSaidIt = blocking?.kind === "your_section";

  const currentIndex = stages.findIndex((s) => s.key === currentStageKey);
  // No current stage means the permit is finished or predates the process
  // engine. The old alert rendered nothing in that case and neither do we —
  // guessing "everything is done" from an empty key would be a claim the data
  // does not support.
  const showTracker = currentIndex >= 0;

  const stateOf = (stage: Stage, index: number): StageState => {
    if (stage.applies === false) return "skipped";
    if (index === currentIndex) return "current";
    return index < currentIndex ? "done" : "upcoming";
  };

  const barColor = (state: StageState) =>
    ({
      done: token.colorSuccess,
      current: token.colorPrimary,
      upcoming: token.colorFillSecondary,
      skipped: token.colorFillQuaternary,
    }[state]);

  const labelColor = (state: StageState) =>
    ({
      done: token.colorTextSecondary,
      current: token.colorText,
      upcoming: token.colorTextTertiary,
      skipped: token.colorTextQuaternary,
    }[state]);

  // Never both: the call to action only exists once nothing is outstanding.
  const showNextStep = !!nextStep && pending.length === 0;

  if (!showTracker && pending.length === 0 && !showNextStep) return null;

  return (
    <div
      style={{
        background: token.colorBgContainer,
        border: `1px solid ${token.colorBorderSecondary}`,
        borderRadius: token.borderRadiusLG,
        padding: 16,
        marginBottom: 16,
      }}
    >
      {blocking && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: showTracker ? 14 : 0,
          }}
        >
          {/* An icon and a weight, not a colour alone: "it is your turn" and
              "somebody else owes it" have to be separable without hue. */}
          {mine ? (
            <BellOutlined style={{ color: token.colorPrimary }} />
          ) : (
            <ClockCircleOutlined style={{ color: token.colorTextTertiary }} />
          )}
          <span
            style={{
              fontSize: 14,
              fontWeight: mine ? 600 : 400,
              color: mine ? token.colorText : token.colorTextSecondary,
            }}
          >
            {t(blocking.message)}
          </span>
        </div>
      )}

      {showTracker && (
        <div style={{ display: "flex", gap: 8, alignItems: "stretch" }}>
          {stages.map((stage, index) => {
            const state = stateOf(stage, index);
            const label = (
              <div
                style={{
                  fontSize: 13,
                  // Weight, not just hue, marks the current step — the colour
                  // alone would be the only cue for anyone who can't separate
                  // these two blues.
                  fontWeight: state === "current" ? 600 : 400,
                  color: labelColor(state),
                  textDecoration: state === "skipped" ? "line-through" : "none",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={t(stage.name)}
              >
                {t(stage.name)}
              </div>
            );

            return (
              <div key={stage.key} style={{ flex: "1 1 0", minWidth: 0 }}>
                <div
                  style={{
                    height: state === "current" ? 4 : 3,
                    borderRadius: 2,
                    background: barColor(state),
                    marginBottom: 6,
                  }}
                />
                {state === "skipped" ? (
                  // Why it was ruled out is the whole point of showing it.
                  <Tooltip title={stage.condition_text}>{label}</Tooltip>
                ) : (
                  label
                )}
                {state === "current" && (
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      letterSpacing: "0.06em",
                      textTransform: "uppercase",
                      color: token.colorPrimary,
                      marginTop: 2,
                    }}
                  >
                    {stage.kind === "Approval"
                      ? `${t("Current stage")} · ${t("Approval")}`
                      : t("Current stage")}
                  </div>
                )}
                {state === "skipped" && (
                  <div
                    style={{
                      fontSize: 11,
                      color: token.colorTextQuaternary,
                      marginTop: 2,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {t("Not required for this request")}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showTracker && (pending.length > 0 || showNextStep) && (
        <div
          style={{
            height: 1,
            background: token.colorSplit,
            margin: "16px 0 12px",
          }}
        />
      )}

      {pending.length > 0 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          {/* The caption is dropped when `blocking` has already said it. Both
              were on screen at once — "You have 'Gas Test' to fill in." in the
              line above, then "You have sections to fill" 30px below it — and
              a reader who has to check whether two sentences mean the same
              thing has been given work, not information. The buttons stay:
              they are the part that does something. */}
          {!blockingSaidIt && (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                color: token.colorWarningText,
                fontWeight: 500,
                fontSize: 14,
              }}
            >
              <WarningOutlined style={{ color: token.colorWarning }} />
              {t("You have sections to fill")}
            </span>
          )}
          {/* Each one jumps to the section rather than describing where it is. */}
          {pending.map((ft: any) => (
            <Button
              key={ft.id}
              size="small"
              icon={<ArrowRightOutlined />}
              onClick={() => onJumpToSection(ft.id)}
            >
              {t(ft.title)}
            </Button>
          ))}
        </div>
      )}

      {showNextStep && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              color:
                nextStep!.tone === "blocked"
                  ? token.colorWarningText
                  : token.colorTextSecondary,
              fontWeight: nextStep!.tone === "blocked" ? 500 : 400,
              fontSize: 14,
            }}
          >
            {nextStep!.tone === "blocked" ? (
              <WarningOutlined style={{ color: token.colorWarning }} />
            ) : (
              <CheckCircleOutlined style={{ color: token.colorSuccess }} />
            )}
            {nextStep!.message}
          </span>
          <Button
            type={nextStep!.tone === "blocked" ? "default" : "primary"}
            size="small"
            icon={
              nextStep!.tone === "blocked" ? <ArrowRightOutlined /> : undefined
            }
            loading={nextStep!.busy}
            onClick={nextStep!.onClick}
          >
            {nextStep!.label}
          </Button>
        </div>
      )}
    </div>
  );
};

export default RequestProgress;
